import {
  FunctionComponent,
  useState,
  useMemo,
  useEffect,
  ReactElement,
  createContext,
  Dispatch,
  SetStateAction,
} from 'react'
import { validate } from 'jsonschema'
import { Box, Typography } from '@mui/material'
import { Loader, ThemedErrorMessage, Translation } from '@dxp-module/common'
import Form, { ISubmitEvent, IChangeEvent } from '@rjsf/core'
import CustomComponentsMapper from '../../../common/components/form/CustomComponentsMapper'
import { useCustomErrorMessagesMapper } from '../../hooks/useCustomErrorMessageMapper'
import useConvertForm from '../../hooks/useConvertForm'
import { loadingInsuranceFormVar } from '../../../common/graphql/cache'
import { GetInsuranceApplicationFormQuery } from '../../../common/graphql/__generated__/graphql.autogenerated'
import ObjectFieldTemplate from './objectFieldTemplate/ObjectFieldTemplate'
import FieldTemplate from './fieldTemplate/FieldTemplate'
import ArrayFieldTemplate from './arrayFieldTemplate/ArrayFieldTemplate'
import SubmitButton from './submitButton/SubmitButton'
import insuranceFormStyles from './insuranceForm.styles'

type InsuranceApplicationFormData = Record<string, unknown>

type InsuranceFormProps = {
  data: GetInsuranceApplicationFormQuery | undefined
  error: Error | undefined
  loading: boolean
  dealId: string
  disclaimers: ReactElement
  formData: object | null
  setFormData: Dispatch<SetStateAction<object | null>>
  handleSubmit: (event: ISubmitEvent<InsuranceApplicationFormData>) => void
  saveError: Error | null
  saveIsLoading: boolean
}

export const FormContext = createContext<{
  setLiveValidate: ((arg: boolean) => void) | null
}>({ setLiveValidate: null })

const InsuranceForm: FunctionComponent<InsuranceFormProps> = ({
  data,
  loading,
  error,
  dealId,
  disclaimers,
  formData,
  setFormData,
  saveError,
  handleSubmit,
  saveIsLoading,
}) => {
  const [schema, setSchema] = useState<object | null>(null)
  const [uiSchema, setUiSchema] = useState<object | null>(null)
  const [isValid, setIsValid] = useState<boolean>(false)
  const [liveValidate, setLiveValidate] = useState<boolean>(false)
  const customWidgets = useMemo(() => CustomComponentsMapper(), [])
  const { convertForm } = useConvertForm()
  const customErrorMessageMapper = useCustomErrorMessagesMapper()
  const styles = insuranceFormStyles()

  useEffect(() => {
    if (!loading && data?.insurance?.loadApplicationForm) {
      loadingInsuranceFormVar(false)
    } else {
      loadingInsuranceFormVar(true)
    }
  }, [loading, data])

  useEffect(() => {
    if (data) {
      const {
        formData: tmpFormData,
        schema: tmpSchema,
        uiSchema: tmpUiSchema,
      } = convertForm(data.insurance.loadApplicationForm)
      setFormData(tmpFormData)
      setSchema(tmpSchema)
      setUiSchema(tmpUiSchema)
    }
  }, [data])

  useEffect(() => {
    if (formData && schema) {
      const validationResult = validate(
        formData as InsuranceApplicationFormData,
        schema
      )
      setIsValid(validationResult.errors.length === 0)
    }
  }, [formData, schema])

  const handleFormChange = (
    event: IChangeEvent<InsuranceApplicationFormData>
  ) => {
    const { formData, errors } = event
    setFormData({ ...formData })
    // Moved here since in transform schema errors it was causing issues
    setIsValid(errors.length === 0)
  }

  if (loading) {
    return <Loader />
  }

  if (saveError || error) {
    return (
      <ThemedErrorMessage
        center={true}
        message={
          'trans__insurance__insapp_insurance_form__general_error_message'
        }
        error={saveError || error}
      />
    )
  }

  return (
    <Box className={styles.root} data-test={`myInsurance:form:${dealId}`}>
      <Typography variant="subtitle1">
        <Translation
          id="trans__insurance__insapp_insurance_form__insurance_form_label"
          className={styles.formTitle}
        />
      </Typography>
      <FormContext.Provider value={{ setLiveValidate }}>
        <Form<InsuranceApplicationFormData>
          schema={schema || {}}
          uiSchema={uiSchema || {}}
          widgets={customWidgets}
          formData={(formData as InsuranceApplicationFormData) || {}}
          onChange={handleFormChange}
          onSubmit={handleSubmit}
          liveValidate={liveValidate}
          liveOmit={true}
          omitExtraData={true}
          showErrorList={false}
          noHtml5Validate={true}
          transformErrors={customErrorMessageMapper}
          FieldTemplate={FieldTemplate}
          ObjectFieldTemplate={ObjectFieldTemplate}
          ArrayFieldTemplate={ArrayFieldTemplate}
        >
          <SubmitButton disabled={!isValid} loading={saveIsLoading} />
        </Form>
      </FormContext.Provider>
      {disclaimers}
    </Box>
  )
}

export default InsuranceForm
